import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import ContactForm from '../components/forms/ContactForm';
import { Breadcrumbs } from '../components/Breadcrumbs';

const NkuPage: React.FC = () => {
  const pageTitle = 'НКУ — низковольтное комплектное устройство: расшифровка, типы, отличие от КРУ';
  const pageDescription =
    'Расшифровка, назначение, типы и отличия от высоковольтных аналогов. Всё о щитах и шкафах для сетей 0,4 кВ.';
  const imageUrl = '/images/blog/nku-featured.jpg'; // Placeholder

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href="https://prominnovations.ru/nku" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={`https://prominnovations.ru${imageUrl}`} />
        <meta property="og:url" content="https://prominnovations.ru/nku" />
        <meta property="og:type" content="article" />
      </Helmet>
      <div className="font-museo-sans">
        <div className="bg-[#f9fcff]">
          <div className="container-custom mx-auto pt-2 lg:pt-5">
            <Breadcrumbs />
          </div>
        </div>

        <main>
          <section className="bg-[#f9fcff]">
            <div className="container-custom mx-auto">
              <h1 className="text-4xl lg:text-5xl font-bold mb-5 bg-gradient-to-r from-[#1a3a4f] to-[#2b5c7a] text-transparent bg-clip-text">
                НКУ — низковольтное комплектное устройство
              </h1>
              <p className="text-xl text-[#2c5a7a] max-w-4xl">
                Расшифровка, назначение, типы и отличия от высоковольтных аналогов. Всё о щитах и
                шкафах для сетей 0,4 кВ.
              </p>
            </div>
          </section>
          <section>
            <div className="container-custom mx-auto">
              <h2 className="text-3xl font-bold mb-6 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
                Что такое НКУ?
              </h2>
              <div className="bg-blue-50 border-l-4 border-blog-accent p-8 rounded-r-2xl my-6">
                <p>
                  <strong className="text-blog-accent font-semibold">
                    НКУ (низковольтное комплектное устройство)
                  </strong>{' '}
                  — это электротехническое изделие заводского изготовления, предназначенное для
                  приёма, распределения, учёта и защиты электрической энергии в сетях напряжением{' '}
                  <strong>до 1000 В</strong> (обычно 0,4 кВ).
                </p>
                <p className="mt-4">
                  <em>Простыми словами:</em> это металлические шкафы или щиты с автоматами,
                  рубильниками, счётчиками, которые устанавливаются на вводе электричества в здание
                  или распределяют питание по цехам, этажам, объектам.
                </p>
                <p className="mt-4">
                  Примеры НКУ: вводно-распределительные устройства (ВРУ), главные распределительные
                  щиты (ГРЩ), этажные щиты, щиты учёта электроэнергии, шкафы управления (ШУ)
                  насосами, вентиляцией и т.д.
                </p>
              </div>
            </div>
          </section>
          <section>
            <div className="container-custom mx-auto">
              <h2 className="text-3xl font-bold mb-6 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
                Расшифровка аббревиатуры
              </h2>
              <div className="flex flex-wrap gap-5 my-5">
                <div className="flex-1 bg-white rounded-2xl p-5 border border-gray-200 min-w-[220px]">
                  <i className="fas fa-charging-station text-blog-accent text-2xl mb-4"></i>
                  <h3 className="font-bold text-lg">Н — Низковольтное</h3>
                  <p className="text-gray-600">
                    Рабочее напряжение не превышает 1000 В переменного тока (обычно 380/220 В).
                  </p>
                </div>
                <div className="flex-1 bg-white rounded-2xl p-5 border border-gray-200 min-w-[220px]">
                  <i className="fas fa-cubes text-blog-accent text-2xl mb-4"></i>
                  <h3 className="font-bold text-lg">К — Комплектное</h3>
                  <p className="text-gray-600">
                    Изготовлено на заводе в виде готовых шкафов или щитов, не требует сборки на
                    месте из отдельных деталей.
                  </p>
                </div>
                <div className="flex-1 bg-white rounded-2xl p-5 border border-gray-200 min-w-[220px]">
                  <i className="fas fa-microchip text-blog-accent text-2xl mb-4"></i>
                  <h3 className="font-bold text-lg">У — Устройство</h3>
                  <p className="text-gray-600">
                    Выполняет функции распределения, учёта, управления и защиты.
                  </p>
                </div>
              </div>
            </div>
          </section>
          <section>
            <div className="container-custom mx-auto">
              <h2 className="text-3xl font-bold mb-6 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
                Назначение и области применения НКУ
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 my-5">
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <i className="fas fa-arrow-left text-2xl text-blog-accent mb-4"></i>
                  <h3 className="font-bold text-lg">Приём электроэнергии</h3>
                  <p className="text-gray-600">
                    От трансформатора (КТПН) или от городской сети 0,4 кВ.
                  </p>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <i className="fas fa-random text-2xl text-blog-accent mb-4"></i>
                  <h3 className="font-bold text-lg">Распределение</h3>
                  <p className="text-gray-600">
                    По группам потребителей (освещение, розетки, оборудование).
                  </p>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <i className="fas fa-chart-simple text-2xl text-blog-accent mb-4"></i>
                  <h3 className="font-bold text-lg">Учёт электроэнергии</h3>
                  <p className="text-gray-600">Установка счётчиков, трансформаторов тока.</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-6">
                  <i className="fas fa-shield-alt text-2xl text-blog-accent mb-4"></i>
                  <h3 className="font-bold text-lg">Защита от КЗ и перегрузки</h3>
                  <p className="text-gray-600">Автоматические выключатели, предохранители, УЗО.</p>
                </div>
              </div>
              <p>
                <strong>Где применяются НКУ:</strong> жилые и административные здания (ВРУ, этажные
                щиты), промышленные предприятия (ГРЩ, шкафы управления), торговые центры, котельные,
                насосные станции, стройплощадки (временные щиты), инфраструктура ЖКХ.
              </p>
            </div>
          </section>
          <section>
            <div className="container-custom mx-auto">
              <h2 className="text-3xl font-bold mb-6 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
                Из чего состоит НКУ?
              </h2>
              <div className="flex flex-wrap gap-5 my-5">
                <div className="flex-1 bg-[#f9fcff] rounded-2xl p-5">
                  <i className="fas fa-toggle-on mr-2"></i>{' '}
                  <strong>Вводные автоматические выключатели</strong> (рубильники) — для отключения
                  всего объекта.
                </div>
                <div className="flex-1 bg-[#f9fcff] rounded-2xl p-5">
                  <i className="fas fa-chart-line mr-2"></i> <strong>Трансформаторы тока</strong> —
                  для подключения счётчиков.
                </div>
                <div className="flex-1 bg-[#f9fcff] rounded-2xl p-5">
                  <i className="fas fa-chart-pie mr-2"></i> <strong>Приборы учёта</strong> —
                  электронные или индукционные счётчики.
                </div>
                <div className="flex-1 bg-[#f9fcff] rounded-2xl p-5">
                  <i className="fas fa-bolt mr-2"></i>{' '}
                  <strong>Шины (сборные и распределительные)</strong> — для соединения автоматов.
                </div>
              </div>
              <p>
                Корпус — металлический, степень защиты от IP20 (для помещений) до IP54 (для улицы).
                Внутри — также могут устанавливаться контакторы, реле, УЗИП (ограничители
                перенапряжений).
              </p>
            </div>
          </section>
          <section>
            <div className="container-custom mx-auto">
              <h2 className="text-3xl font-bold mb-6 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
                Основные типы низковольтных устройств
              </h2>
              <ul className="flex flex-wrap gap-4 my-5 list-none">
                <li className="bg-gray-100 rounded-full px-6 py-2">
                  <strong>ВРУ (вводно-распределительное устройство)</strong> — на вводе в здание или
                  квартал (0,4 кВ).
                </li>
                <li className="bg-gray-100 rounded-full px-6 py-2">
                  <strong>ГРЩ (главный распределительный щит)</strong> — в промышленности, крупных
                  объектах.
                </li>
                <li className="bg-gray-100 rounded-full px-6 py-2">
                  <strong>ЩР (щит распределительный)</strong> — этажные, для отдельных помещений.
                </li>
                <li className="bg-gray-100 rounded-full px-6 py-2">
                  <strong>ЩУ (щит учёта)</strong> — коммерческий учёт электроэнергии.
                </li>
                <li className="bg-gray-100 rounded-full px-6 py-2">
                  <strong>ШУ (шкаф управления)</strong> — для управления насосами, вентиляторами,
                  конвейерами.
                </li>
                <li className="bg-gray-100 rounded-full px-6 py-2">
                  <strong>ЩА (щит автоматики)</strong> — для систем диспетчеризации, АВР.
                </li>
                <li className="bg-gray-100 rounded-full px-6 py-2">
                  <strong>ЩО (щит освещения)</strong> — распределение освещения.
                </li>
              </ul>
              <p>
                Все они относятся к классу НКУ и изготавливаются по ГОСТ 22789-94, ГОСТ Р
                51321.1-2007.
              </p>
            </div>
          </section>
          <section>
            <div className="container-custom mx-auto">
              <h2 className="text-3xl font-bold mb-6 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
                Отличие НКУ от КРУ и КСО
              </h2>
              <div className="bg-gray-50 rounded-2xl p-6 my-5">
                <p>
                  <strong className="text-blog-accent font-semibold">
                    Главное различие — класс напряжения.
                  </strong>
                </p>
                <p className="mt-4">
                  <strong>НКУ</strong> работает при напряжении <strong>до 1000 В</strong> (обычно
                  380/220 В). Это щиты и шкафы, которые питают электроприёмники напрямую.
                </p>
                <p className="mt-3">
                  <strong>КРУ и КСО</strong> — это высоковольтные устройства (6–35 кВ). Они
                  устанавливаются на стороне высокого напряжения подстанции, перед трансформатором.
                  КРУ и КСО управляют питанием трансформаторов, а НКУ — распределяют электроэнергию
                  уже после трансформатора.
                </p>
                <p className="mt-4">
                  <i className="fas fa-arrow-right text-blog-accent mr-2"></i>{' '}
                  <Link to="/catalog/switchgear" className="text-blue-600 hover:underline">
                    Подробнее о КРУ (6–35 кВ)
                  </Link>
                </p>
                <p>
                  <i className="fas fa-arrow-right text-blog-accent mr-2"></i>{' '}
                  <Link to="/catalog/kso" className="text-blue-600 hover:underline">
                    Подробнее о КСО (6–10 кВ)
                  </Link>
                </p>
                <p className="mt-3">
                  В КТПН, как правило, установлены: КРУ или КСО на стороне 6–10 кВ, силовой
                  трансформатор, и затем <strong>НКУ (щит 0,4 кВ)</strong> — для выдачи питания на
                  объекты.
                </p>
              </div>
            </div>
          </section>
          <section>
            <div className="container-custom mx-auto">
              <h2 className="text-3xl font-bold mb-6 relative inline-block after:content-[''] after:absolute after:bottom-[-8px] after:left-0 after:w-16 after:h-1 after:bg-blog-accent after:rounded">
                Связанные термины
              </h2>
              <div className="flex flex-wrap gap-4 my-5">
                <div className="bg-white border border-gray-200 rounded-2xl p-4 flex-1 min-w-[180px]">
                  <Link
                    to="/catalog/switchgear"
                    className="font-semibold text-blue-600 hover:underline"
                  >
                    КРУ
                  </Link>
                  <br />
                  <span className="text-sm text-gray-500">Высоковольтное до 35 кВ</span>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-4 flex-1 min-w-[180px]">
                  <Link to="/catalog/kso" className="font-semibold text-blue-600 hover:underline">
                    КСО
                  </Link>
                  <br />
                  <span className="text-sm text-gray-500">6–10 кВ, одностороннее обслуживание</span>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-4 flex-1 min-w-[180px]">
                  <Link to="/ktpn-kiosk" className="font-semibold text-blue-600 hover:underline">
                    КТПН
                  </Link>
                  <br />
                  <span className="text-sm text-gray-500">С НКУ на 0,4 кВ</span>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-4 flex-1 min-w-[180px]">
                  <Link
                    to="/catalog/transformers"
                    className="font-semibold text-blue-600 hover:underline"
                  >
                    Трансформатор силовой
                  </Link>
                  <br />
                  <span className="text-sm text-gray-500">Питает НКУ</span>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-4 flex-1 min-w-[180px]">
                  <Link
                    to="/nominalnoe-napryazhenie"
                    className="font-semibold text-blue-600 hover:underline"
                  >
                    Номинальная мощность
                  </Link>
                  <br />
                  <span className="text-sm text-gray-500">Расчёт нагрузки</span>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-4 flex-1 min-w-[180px]">
                  <Link
                    to="/blog/chto-takoe-stepen-zashchity-ip"
                    className="font-semibold text-blue-600 hover:underline"
                  >
                    Степень защиты IP
                  </Link>
                  <br />
                  <span className="text-sm text-gray-500">IP20…IP54</span>
                </div>
              </div>
              <div className="mt-10">
                <Link to="/ktpn-promo" className="btn-secondary">
                  <i className="fas fa-arrow-left"></i> КТПН 63 кВА (с НКУ)
                </Link>
                <Link to="/catalog/kso" className="btn-secondary ml-4">
                  <i className="fas fa-exchange-alt"></i> Перейти к КСО
                </Link>
              </div>
            </div>
          </section>
        </main>

        <div className="container-custom mx-auto pb-5 lg:pb-10">
          <ContactForm />
        </div>
      </div>
    </>
  );
};

export default NkuPage;
